import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  useEffect,
  createContext,
  useContext,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { createAuthAxios } from './customAxios';
import {
  resetLastSuccessfulLoginOrganizationId,
  storeLastSuccessfulLoginOrganizationId,
} from '../auth/auth0Storage';
import { useHistory, useLocation } from 'react-router-dom';
import { resolveUserAgent } from './user-agent';
import { TokenCustomClaimKeysEnum } from '@sitecore-ui/portal-singular';

const publicRoutes = [
  '/no-organization-access',
  '/no-valid-invitation',
  '/something-went-wrong',
];

// Auth0's transaction storage only tracks one pending redirect at a time, so two
// concurrent loginWithRedirect calls can cancel each other out (PORTAL-5988).
let isAuthRedirectInFlight = false;

// Transient silent-auth failures (session restore, token lock/iframe timeout); withAuthenticationRequired recovers.
const recoverableSilentAuthErrors = [
  'login_required',
  'consent_required',
  'interaction_required',
  'timeout',
];

export const AxiosContext = createContext(axios.create());

// Needs to be used inside Auth0Context
export const AuthenticatedAxiosProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();
  const { replace, location } = useHistory();
  const organizationId = user?.[TokenCustomClaimKeysEnum.ORG_ID];

  const { userAgent } = resolveUserAgent();

  // TODO: need to revisit when organization won't be in the token
  useEffect(() => {
    const isPublicRoute = publicRoutes.includes(location.pathname);
    if (!organizationId || isPublicRoute) return;

    storeLastSuccessfulLoginOrganizationId(organizationId);

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('organization', organizationId);

    replace({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const authAxios = createAuthAxios(
    axios.create({
      headers: {
        ...userAgent,
      },
    }),
  );
  usePortalLocationStorage();

  authAxios.interceptors.request.use(async (request) => {
    try {
      const token = await getAccessTokenSilently({
        organization_id: organizationId,
        tenant_id: undefined,
      });
      request &&
        request.headers &&
        (request.headers.Authorization = 'Bearer ' + token);

      return request;
    } catch (e) {
      const authErrorCode = (e as { error?: string })?.error;
      const isRecoverable = recoverableSilentAuthErrors.includes(
        authErrorCode ?? '',
      );

      if (!isRecoverable && !isAuthRedirectInFlight) {
        isAuthRedirectInFlight = true;
        resetLastSuccessfulLoginOrganizationId();
        try {
          await loginWithRedirect({
            tenant_id: undefined,
          });
        } finally {
          isAuthRedirectInFlight = false;
        }
      }
      throw e;
    }
  });

  return (
    <AxiosContext.Provider value={authAxios}>{children}</AxiosContext.Provider>
  );
};

export const useAuthenticatedAxios = () => {
  const axiosInstance = useContext(AxiosContext);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!axiosInstance)
    throw new Error(
      'useAuthenticatedAxios can not be used outside AuthenticatedAxiosProvider context',
    );
  return axiosInstance;
};

export function usePortalLocationStorage() {
  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);
  const organization = params.get('organization');
  const error = params.get('error');
  const code = params.get('code');
  const invitation = params.get('invitation');
  // The callback URL is cleaned before auth finishes loading, so remember it from the first render.
  const isRedirectCallback = useRef(Boolean(code));

  const { loginWithRedirect, isLoading, isAuthenticated, user } = useAuth0();
  const tokenOrganizationId = user?.[TokenCustomClaimKeysEnum.ORG_ID];

  const switchOrg = useCallback(
    (organization: string | undefined | null) => {
      if (isAuthRedirectInFlight) return Promise.resolve();
      isAuthRedirectInFlight = true;

      return loginWithRedirect({
        organization_id: organization || undefined,
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}`,
        },
      })
        .then(
          () =>
            organization &&
            storeLastSuccessfulLoginOrganizationId(organization as string),
        )
        .finally(() => {
          isAuthRedirectInFlight = false;
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Unauthenticated users are redirected by withAuthenticationRequired; only re-login on an org mismatch.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (isRedirectCallback.current || invitation || error) return;
    if (publicRoutes.includes(pathname)) return;

    if (organization && organization !== tokenOrganizationId) {
      switchOrg(organization);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, tokenOrganizationId, invitation]);
}
